import React, { useState } from "react";
import { ClipboardCheck, AlertTriangle, ChevronDown } from "lucide-react";

const SEVERITY = {
  high: { label: "Serious", bg: "hsl(15 62% 95%)", fg: "hsl(15 62% 32%)", br: "hsl(15 62% 70%)" },
  medium: { label: "Worth a look", bg: "hsl(38 70% 94%)", fg: "hsl(38 60% 28%)", br: "hsl(38 70% 65%)" },
  low: { label: "Minor", bg: "hsl(210 20% 95%)", fg: "hsl(210 15% 35%)", br: "hsl(210 15% 75%)" },
};

function toneFor(score) {
  if (score >= 85) return "hsl(145 40% 40%)";
  if (score >= 65) return "hsl(38 70% 45%)";
  return "hsl(15 62% 52%)";
}

/**
 * RewriteReport — the editorial audit of a rewrite: did it keep the meaning,
 * the facts and the voice. Shown only for plans that run the audit.
 */
export default function RewriteReport({ audit }) {
  const [open, setOpen] = useState(false);
  if (!audit) return null;
  const { dimensions = [], defects = [], overall, integrity_failed: integrityFailed } = audit;

  return (
    <div data-testid="rewrite-report" className="mt-4 surface p-5 fade-up">
      <button
        data-testid="rewrite-report-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <ClipboardCheck size={16} className="text-[hsl(var(--primary))] shrink-0" />
          <div className="min-w-0">
            <div className="label-eyebrow">Rewrite report</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">
              {defects.length === 0
                ? "Meaning, facts and voice all checked out."
                : `${defects.length} thing${defects.length > 1 ? "s" : ""} worth reading before you use this`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span
            data-testid="rewrite-report-overall"
            className="font-display text-xl"
            style={{ color: toneFor(overall) }}
          >
            {overall}
          </span>
          <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {integrityFailed && (
        <div
          data-testid="integrity-warning"
          className="mt-4 rounded-xl p-3 text-sm flex gap-2.5 items-start"
          style={{ background: "hsl(15 62% 95%)", border: "1px solid hsl(15 62% 70%)", color: "hsl(15 62% 28%)" }}
        >
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>
            <strong>Check the details before you submit this.</strong> The rewrite may have added or dropped a fact
            that wasn't in your original. Read the flagged items below.
          </span>
        </div>
      )}

      {open && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {dimensions.map((d) => (
            <div key={d.key} data-testid={`audit-${d.key.replace(/_/g, "-")}`} className="min-w-0">
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <span className="text-xs text-[hsl(var(--muted-foreground))]">{d.label}</span>
                <span className="text-[11px] font-mono-editor tabular-nums" style={{ color: toneFor(d.score) }}>
                  {d.score}
                </span>
              </div>
              <div className="h-1.5 w-full bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${d.score}%`, background: toneFor(d.score), transition: "width 700ms ease-out" }}
                />
              </div>
              {d.note && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5 leading-snug">{d.note}</div>}
            </div>
          ))}
        </div>
      )}

      {defects.length > 0 && (
        <ul className="mt-5 space-y-2.5">
          {defects.map((d, i) => {
            const s = SEVERITY[d.severity] || SEVERITY.low;
            return (
              <li key={i} data-testid={`audit-defect-${i}`} className="text-sm flex gap-2.5 items-start">
                <span
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 mt-0.5"
                  style={{ background: s.bg, color: s.fg, border: `1px solid ${s.br}` }}
                >
                  {s.label}
                </span>
                <span className="text-[hsl(var(--muted-foreground))]">{d.detail}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
